// Modul 40 B4 — die Zeilen im Rumpf einer Bestell-Karte.
//
// Eine Zeile = eine Sache, die im Einkauf besorgt werden muss: die Rechnung, die Zahlungen,
// jeder Container mit seinen Terminen und Nebenkosten, die Packliste, der Rest, die Notiz.
// Jede Zeile bringt ihre eigenen Schreibwege mit; die Karte (BestellKarte.tsx) setzt sie
// nur zusammen. Was geschrieben wird, laeuft ausnahmslos ueber die Zellen aus B3 — es gibt
// in diesem Modul kein zweites Eingabemuster.
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/api/auth";
import {
  paymentStatusLabel, shipmentStatusLabel, weStatusLabel, weStatusBadgeClass,
  SHIPMENT_STATUS_OPTIONS,
} from "@/lib/purchase-format";
import {
  usePatchPurchaseOrder, usePatchPurchasePayment, useAddPurchasePayment, useDeletePurchasePayment,
  usePatchPurchaseShipment, useSetPurchaseShipmentStatus, useAddPurchaseCost, usePatchPurchaseCost,
  useAssignDocument, useConfirmShipmentDelivery,
} from "@/api/purchase";
import type {
  PurchaseCost, PurchaseDocument, PurchaseOrderFile, PurchasePayment, PurchaseShipment,
} from "@/api/purchase";
import { InlineBetrag, InlineDatum, InlineText, DateiZelle, DokumentChip, serverSatz } from "./InlineZellen";
import { dokumenteFuerContainer, terminLeiste, parseBetrag, zustellErgebnisText } from "./zeilen";

const PREISGRUND = "Betragsfelder brauchen die Einkaufs-Preisrechte";
/** Diese beiden Status behaupten einen Betrag — ohne Betrag lehnt der Server sie ab. */
const BRAUCHT_BETRAG = new Set(["erfasst", "geschaetzt"]);
const KOSTEN_STATUS = ["erwartet", "geschaetzt", "erfasst", "entfaellt"] as const;

/** Ein Zeilengeruest: links das Wort, rechts der Inhalt. Haelt die Karte lesbar. */
export function Zeile(p: { titel: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line/70 py-1.5 text-xs">
      <span className="w-28 shrink-0 font-medium text-ink-2">{p.titel}</span>
      {p.children}
    </div>
  );
}

function Hinweis({ text }: { text: string }) {
  return <span role="alert" className="text-xs text-destructive">{text}</span>;
}

export function InvoiceZeile(p: {
  file: PurchaseOrderFile; docs: PurchaseDocument[]; darfPreise: boolean;
}) {
  const patch = usePatchPurchaseOrder(p.file.id);
  return (
    <Zeile titel="Rechnung">
      <InlineBetrag
        value={p.file.invoice_total_usd}
        waehrung="USD"
        ariaLabel="Rechnungssumme USD"
        disabled={!p.darfPreise}
        disabledGrund={PREISGRUND}
        onSave={(v) => patch.mutateAsync({ invoice_total_usd: v })}
      />
      {p.file.payment_status ? (
        <span className="rounded bg-background px-1.5 py-0.5 text-ink-2">
          {paymentStatusLabel(p.file.payment_status)}
        </span>
      ) : null}
      <DateiZelle orderId={p.file.id} shipmentId={null} docs={p.docs} ariaLabel="Rechnungsdatei hinzufügen" />
    </Zeile>
  );
}

function ZahlungZeile(p: { zahlung: PurchasePayment; nr: number; darfPreise: boolean }) {
  const patch = usePatchPurchasePayment();
  const loeschen = useDeletePurchasePayment();
  // Absage des Servers zum Loeschen (Modul 41: 409 „Zahlung hat Container-Zuordnungen — zuerst
  // lösen"). Bis Modul 42 hatte der Aufruf keinen `onError` — die Zeile blieb wortlos stehen,
  // und der Nutzer hielt das nicht ausgefuehrte Loeschen fuer einen Anzeigefehler.
  const [fehler, setFehler] = useState<string | null>(null);
  // `loeschen.isPending` allein reicht NICHT: es wird erst im naechsten Render wahr, zwei
  // schnelle Klicks laufen also beide durch (gemessen: 2 Loeschungen). Der zweite scheitert
  // dann mit 404, NACHDEM der erste gelungen ist — die Zeile meldete eine Absage fuer einen
  // Vorgang, der geklappt hat. Ein Ref wirkt sofort, ohne auf React zu warten.
  const laeuft = useRef(false);
  const z = p.zahlung;
  // Nur das EINE geaenderte Feld — der Typ ist serverseitig gesperrt, ein voller Datensatz
  // waere hier ein 400 (siehe usePatchPurchasePayment).
  const feld = (name: string) => (v: number | string | null) =>
    patch.mutateAsync({ id: z.id, body: { [name]: v } });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="w-20 text-ink-2">{z.typ}</span>
      <InlineBetrag value={z.amount_usd} waehrung="USD" ariaLabel={`Zahlung ${p.nr} USD`}
        disabled={!p.darfPreise} disabledGrund={PREISGRUND} onSave={feld("amount_usd")} />
      <InlineBetrag value={z.amount_eur} waehrung="EUR" ariaLabel={`Zahlung ${p.nr} EUR`}
        disabled={!p.darfPreise} disabledGrund={PREISGRUND} onSave={feld("amount_eur")} />
      {/* Datum und Beleg gehoeren zum Zahlungsvorgang so gut wie der Betrag: wer den Beleg
          umschreibt oder das Zahldatum verschiebt, aendert den Geldstand der Bestellung.
          Beide hingen bis zum Whole-Branch-Review an keinem Recht (Befund Reviewer 1). */}
      <InlineDatum value={z.paid_at} ariaLabel={`Zahlung ${p.nr} Datum`}
        disabled={!p.darfPreise} disabledGrund={PREISGRUND} onSave={feld("paid_at")} />
      <InlineText value={z.beleg_ref} ariaLabel={`Zahlung ${p.nr} Beleg`}
        disabled={!p.darfPreise} disabledGrund={PREISGRUND} onSave={feld("beleg_ref")} />
      <button
        type="button"
        aria-label={`Zahlung ${p.nr} löschen`}
        // Das Loeschen ist der schaerfste Geld-Schreibweg der Zeile — es nimmt einen
        // gezahlten Betrag samt Container-Zuordnungen wieder heraus. `isPending` graut den
        // Knopf aus, solange der Aufruf laeuft; die Sperre gegen den zweiten Klick ist aber
        // `laeuft` oben — das Attribut kommt einen Render zu spaet.
        disabled={!p.darfPreise || loeschen.isPending}
        title={p.darfPreise ? undefined : PREISGRUND}
        className="text-ink-2 hover:text-destructive disabled:opacity-50"
        onClick={() => {
          // Eine Zahlung traegt Zuordnungen zu Containern — das Loeschen ist nicht
          // rueckholbar, deshalb die Rueckfrage.
          if (laeuft.current) return;
          if (!window.confirm(`Zahlung ${p.nr} wirklich löschen?`)) return;
          // Erst HINTER der Rueckfrage: bricht der Nutzer ab, ist nichts passiert — dann muss
          // die Begruendung des letzten Versuchs stehen bleiben. Sie stand bis zum
          // Whole-Branch-Review davor und verschwand schon beim blossen Klick auf den
          // Muelleimer; wer sie noch einmal lesen wollte, loeschte sie damit selbst.
          // Weggeraeumt wird sie aber weiterhin VOR dem neuen Aufruf und nicht erst danach:
          // sonst waere eine wortgleiche zweite Absage fuer Bildschirmleser gar keine neue
          // Meldung mehr.
          setFehler(null);
          laeuft.current = true;
          loeschen.mutate(z.id, {
            onError: (err) => setFehler(serverSatz(err, "Zahlung konnte nicht gelöscht werden")),
            onSettled: () => {
              laeuft.current = false;
            },
          });
        }}
      >
        <Trash2 className="size-3" />
      </button>
      {fehler ? <Hinweis text={fehler} /> : null}
    </div>
  );
}

export function ZahlungenZeile(p: { file: PurchaseOrderFile; darfPreise: boolean }) {
  const anlegen = useAddPurchasePayment(p.file.id);
  const [typ, setTyp] = useState("anzahlung");
  const [usd, setUsd] = useState("");
  const [eur, setEur] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);

  return (
    <Zeile titel="Zahlungen">
      <div className="flex flex-col gap-1">
        {p.file.payments.map((z, i) => (
          <ZahlungZeile key={z.id} zahlung={z} nr={i + 1} darfPreise={p.darfPreise} />
        ))}
        {p.file.payments.length === 0 ? <span className="text-ink-2">Noch keine Zahlung erfasst.</span> : null}
        {p.darfPreise ? (
          <div className="flex flex-wrap items-center gap-1 pt-0.5">
            <select
              aria-label="Neue Zahlung Typ" value={typ} onChange={(e) => setTyp(e.target.value)}
              className="rounded border border-line bg-background px-1.5 py-0.5 text-xs"
            >
              {["anzahlung", "balance", "neben"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input aria-label="Neue Zahlung USD" inputMode="decimal" value={usd}
              onChange={(e) => setUsd(e.target.value)} placeholder="USD" className="h-7 w-24 text-xs" />
            <Input aria-label="Neue Zahlung EUR" inputMode="decimal" value={eur}
              onChange={(e) => setEur(e.target.value)} placeholder="EUR" className="h-7 w-24 text-xs" />
            <Button
              size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs"
              disabled={anlegen.isPending}
              onClick={() => {
                // Derselbe Leser wie in den Zellen. Vorher stand hier
                // `Number(s.replace(",", "."))` — daraus wurde aus "1.000" die Zahl 1 und aus
                // "1.234,50" ein NaN, das als leerer Betrag durchging (Befund beider Reviewer).
                const a = parseBetrag(usd);
                const b = parseBetrag(eur);
                if (a === undefined || b === undefined) {
                  setMeldung("Betrag nicht lesbar (z. B. 1.234,50)");
                  return;
                }
                // Beide Felder leer hiess bisher: eine Zahlung ueber nichts wird angelegt.
                // Sie taucht danach in der Liste auf, zaehlt aber nirgends mit — und wer
                // sie wieder loswerden will, braucht das Loeschen (Befund Reviewer 1).
                if (a === null && b === null) {
                  setMeldung("Mindestens einen Betrag eintragen");
                  return;
                }
                setMeldung(null);
                anlegen.mutate(
                  { typ, amount_usd: a, amount_eur: b },
                  {
                    onSuccess: () => { setUsd(""); setEur(""); },
                    // Ohne diesen Zweig sprangen die Felder bei einer Absage einfach
                    // zurueck und niemand erfuhr, warum (Befund beider Reviewer).
                    onError: (err) => setMeldung(serverSatz(err, "Zahlung konnte nicht angelegt werden")),
                  },
                );
              }}
            >
              <Plus className="size-3" /> Zahlung
            </Button>
            {meldung ? <Hinweis text={meldung} /> : null}
          </div>
        ) : null}
      </div>
    </Zeile>
  );
}

/**
 * Ein Nebenkosten-Posten (Spedition oder Zoll) eines Containers.
 *
 * Der Server nimmt hier KEINEN Teil-Patch: `patchPurchaseCost` ueberschreibt alle vier
 * Felder, und `erfasst`/`geschaetzt` ohne Betrag lehnt er ab. Deshalb schickt jede
 * Aenderung den VOLLEN Rumpf aus dem gerade geladenen Posten — und die beiden Faelle, die
 * der Server ablehnen wuerde, werden hier vorher abgefangen und benannt, statt sie als
 * "400" zurueckkommen zu lassen.
 *
 * Es gibt bewusst KEIN eigenes Haekchen "geprueft": der Status `erfasst` IST die Aussage
 * "Betrag steht" (Entscheid E-5).
 */
function KostenPosten(p: {
  posten: PurchaseCost | undefined; shipmentId: number; typ: "spedition" | "zoll";
  containerNr: number; darfPreise: boolean;
}) {
  const patch = usePatchPurchaseCost();
  const anlegen = useAddPurchaseCost();
  const [meldung, setMeldung] = useState<string | null>(null);
  const wort = p.typ === "spedition" ? "Spedition" : "Zoll";
  const k = p.posten;

  if (!k) {
    return (
      <>
      <button
        type="button"
        aria-label={`Kosten-Posten ${wort} Container ${p.containerNr} anlegen`}
        // Auch das ANLEGEN ist ein Geld-Schreibweg: der Posten entsteht in der
        // Kosten-Tabelle und faellt in jede Nebenkosten-Summe. Ohne Preisrecht waren Betrag
        // und Status gesperrt, der Knopf daneben aber offen (Befund beider Reviewer).
        disabled={!p.darfPreise || anlegen.isPending}
        title={p.darfPreise ? undefined : PREISGRUND}
        className="rounded px-1 text-xs text-ink-2 underline decoration-dotted underline-offset-4 hover:bg-background disabled:opacity-50"
        onClick={() => anlegen.mutate(
          {
            shipmentId: p.shipmentId,
            body: { typ: p.typ, label: null, amount_eur: null, status: "erwartet" },
          },
          { onError: (err) => setMeldung(serverSatz(err, "Posten konnte nicht angelegt werden")) },
        )}
      >
        + Posten
      </button>
      {meldung ? <Hinweis text={meldung} /> : null}
      </>
    );
  }

  const vollerRumpf = (amount_eur: number | null, status: string) =>
    ({ typ: k.typ, label: k.label, amount_eur, status });

  return (
    <>
      <InlineBetrag
        value={k.amount_eur} waehrung="EUR"
        ariaLabel={`Kosten ${wort} Container ${p.containerNr} EUR`}
        // Waehrend ein Rumpf unterwegs ist, bleibt die zweite Zelle zu: beide schicken den
        // VOLLEN Datensatz, und der zweite kennt den Stand des ersten noch nicht — er
        // wuerde ihn zurueckdrehen (Befund beider Reviewer).
        disabled={!p.darfPreise || patch.isPending}
        disabledGrund={patch.isPending ? "Wird gespeichert …" : PREISGRUND}
        onSave={async (v) => {
          setMeldung(null);
          if (v === null && BRAUCHT_BETRAG.has(k.status)) {
            setMeldung("Betrag leeren nur bei Status erwartet/entfällt");
            return;
          }
          await patch.mutateAsync({ id: k.id, body: vollerRumpf(v, k.status) });
        }}
      />
      <select
        aria-label={`Kosten-Status ${wort} Container ${p.containerNr}`}
        value={k.status}
        disabled={!p.darfPreise || patch.isPending}
        onChange={(e) => {
          const neu = e.target.value;
          setMeldung(null);
          if (BRAUCHT_BETRAG.has(neu) && k.amount_eur === null) {
            // Kein Request: der Server lehnte ihn ab, und das Auswahlfeld faellt von selbst
            // auf den geladenen Status zurueck (es haengt am Wert, nicht an einem Entwurf).
            setMeldung("Erst Betrag eintragen");
            return;
          }
          patch.mutate(
            { id: k.id, body: vollerRumpf(k.amount_eur, neu) },
            // Der Betrag daneben laeuft ueber die Zelle und zeigt den Satz des Servers
            // selbst; dieses Auswahlfeld verschluckte ihn (Befund Reviewer 2).
            { onError: (err) => setMeldung(serverSatz(err, "Status konnte nicht gesetzt werden")) },
          );
        }}
        className="rounded border border-line bg-background px-1.5 py-0.5 text-xs disabled:opacity-50"
      >
        {KOSTEN_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {meldung ? <Hinweis text={meldung} /> : null}
    </>
  );
}

/**
 * Ein Container mit Termin-Leiste und seinen beiden Kosten-Unterzeilen.
 *
 * Die Termin-Leiste ist die eigentliche Aussage der Karte: vier Punkte CRD → ETD → ETA →
 * Door, gefuellt sobald ein Termin gesetzt UND erreicht ist (Berliner Kalendertag, eine
 * Regel aus B2). Wer auf die Karte schaut, sieht daran ohne Lesen, wie weit die Ware ist.
 */
export function ContainerZeile(p: {
  shipment: PurchaseShipment; nr: number; orderId: number; orderNumber: string;
  docs: PurchaseDocument[]; darfPreise: boolean; heute: string;
}) {
  const patchShipment = usePatchPurchaseShipment();
  const setStatus = useSetPurchaseShipmentStatus();
  const delivery = useConfirmShipmentDelivery(p.shipment.id);
  const auth = useAuth();
  const [meldung, setMeldung] = useState<string | null>(null);
  const actionRef = useRef(false);
  const s = p.shipment;
  const schritte = terminLeiste(s, p.heute);
  const darfZustellen = !!auth.user?.permissions?.article_purchase
    && !!auth.user?.permissions?.warehouse_lead;
  const eingelagerterAltdatenzustand = s.status === "eingelagert" && !s.warehouse_receipt_id;
  const brauchtWareneingang = !s.warehouse_receipt_id
    && s.status !== "im_wareneingang"
    && s.status !== "eingelagert";

  return (
    <Zeile titel={`#${p.orderNumber}-${p.nr}`}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <InlineText
            value={s.label} ariaLabel={`Container ${p.nr} Bezeichnung`}
            onSave={(v) => patchShipment.mutateAsync({ id: s.id, body: { label: v } })}
          />
          <select
            aria-label={`Container ${p.nr} Status`} value={s.status}
            // Ohne die Sperre konnte man im Auswahlfeld hin- und herschalten und mehrere
            // Statuswechsel gleichzeitig losschicken; welcher zuletzt in der Datenbank
            // steht, entschied dann die Reihenfolge der Antworten (Befund beider Reviewer).
            disabled={setStatus.isPending || delivery.isPending}
            onChange={(e) => {
              if (actionRef.current) return;
              actionRef.current = true;
              setMeldung(null);
              setStatus.mutate(
                { id: s.id, status: e.target.value },
                {
                  onError: (err) => setMeldung(serverSatz(err, "Status konnte nicht gesetzt werden")),
                  onSettled: () => { actionRef.current = false; },
                },
              );
            }}
            className="rounded border border-line bg-background px-1.5 py-0.5 text-xs disabled:opacity-50"
          >
            {SHIPMENT_STATUS_OPTIONS.map((st) => (
              <option
                key={st}
                value={st}
                disabled={(st === "zugestellt" || st === "im_wareneingang") && st !== s.status}
              >
                {shipmentStatusLabel(st)}
              </option>
            ))}
          </select>
          {meldung ? <Hinweis text={meldung} /> : null}
          {s.we_status ? (
            <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${weStatusBadgeClass(s.we_status)}`}>
              WE {weStatusLabel(s.we_status)}
            </span>
          ) : null}
        </div>

        {eingelagerterAltdatenzustand ? (
          <div className="flex flex-wrap items-center gap-2">
            <Hinweis text="Container ist als eingelagert markiert, aber ohne Wareneingang verknüpft. Status und Verknüpfung bitte in der Akte prüfen und korrigieren." />
          </div>
        ) : brauchtWareneingang ? (
          <div className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-950">
            <span className="font-medium">
              {s.status === "zugestellt" ? "Zustellung erfasst, Wareneingang noch offen." : "Ware angekommen?"}
            </span>
            {darfZustellen ? (
              <Button
                type="button"
                size="sm"
                disabled={delivery.isPending || setStatus.isPending}
                onClick={() => {
                  if (actionRef.current) return;
                  const frage = s.status === "zugestellt"
                    ? "Wareneingang für diesen Container erneut anlegen?"
                    : "Zustellung bestätigen und Wareneingang für diesen Container anlegen?";
                  if (!window.confirm(frage)) return;
                  actionRef.current = true;
                  setMeldung(null);
                  delivery.mutate(undefined, {
                    onSuccess: (res) => setMeldung(zustellErgebnisText(res)),
                    onError: (err) => setMeldung(serverSatz(err, "Zustellung konnte nicht bestätigt werden")),
                    onSettled: () => { actionRef.current = false; },
                  });
                }}
              >
                {delivery.isPending
                  ? "Wird angelegt …"
                  : s.status === "zugestellt"
                    ? "Wareneingang erneut anlegen"
                    : "Zustellung + Wareneingang"}
              </Button>
            ) : (
              <span>Die Lagerleitung bestätigt die Zustellung und legt den Wareneingang an.</span>
            )}
          </div>
        ) : s.status === "im_wareneingang" && !s.warehouse_receipt_id ? (
          <div className="flex flex-wrap items-center gap-2">
            <Hinweis text="Status Wareneingang ohne verknüpften Wareneingang." />
            {darfZustellen ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={delivery.isPending || setStatus.isPending}
                onClick={() => {
                  if (actionRef.current) return;
                  actionRef.current = true;
                  setMeldung(null);
                  setStatus.mutate(
                    { id: s.id, status: "zugestellt" },
                    {
                      onSuccess: () => setMeldung("Status repariert. Wareneingang kann jetzt angelegt werden."),
                      onError: (err) => setMeldung(serverSatz(err, "Statuskorrektur konnte nicht gespeichert werden")),
                      onSettled: () => { actionRef.current = false; },
                    },
                  );
                }}
              >
                Auf „Zugestellt“ korrigieren
              </Button>
            ) : (
              <span>Bitte die Lagerleitung in der Akte korrigieren lassen.</span>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
          {schritte.map((sch, i) => (
            <span key={sch.key} className="flex items-center gap-1">
              {i > 0 ? <span aria-hidden className="h-px w-3 bg-line" /> : null}
              <span
                aria-hidden
                className={`size-1.5 rounded-full ${sch.erreicht ? "bg-primary" : "bg-line"}`}
              />
              <span className={sch.erreicht ? "font-medium text-ink" : "text-ink-2"}>{sch.label}</span>
              <InlineDatum
                value={sch.datum}
                ariaLabel={`${sch.label} Container ${p.nr}`}
                onSave={(v) => patchShipment.mutateAsync({ id: s.id, body: { [sch.key]: v } })}
              />
            </span>
          ))}
        </div>

        {(["spedition", "zoll"] as const).map((typ) => (
          <div key={typ} className="flex flex-wrap items-center gap-2">
            <span className="w-24 text-ink-2">{typ === "spedition" ? "Rechnung Spedition" : "Zoll"}</span>
            <KostenPosten
              posten={s.costs.find((c) => c.typ === typ)}
              shipmentId={s.id} typ={typ} containerNr={p.nr} darfPreise={p.darfPreise}
            />
            <DateiZelle
              orderId={p.orderId}
              shipmentId={s.id}
              docs={dokumenteFuerContainer(p.docs, s.id, typ)}
              ariaLabel={`${typ === "spedition" ? "Spediteur-Rechnung" : "Zollbeleg"} Container ${p.nr} hinzufügen`}
            />
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-24 text-ink-2">Packliste</span>
          <DateiZelle
            orderId={p.orderId}
            shipmentId={s.id}
            docs={dokumenteFuerContainer(p.docs, s.id, "packliste")}
            ariaLabel={`Packliste Container ${p.nr} hinzufügen`}
          />
        </div>
      </div>
    </Zeile>
  );
}

/** Nicht zugeordnete Bestelldokumente bleiben sichtbar und Packlisten reparierbar. */
export function SonstigeZeile(p: {
  docs: PurchaseDocument[];
  shipments: PurchaseShipment[];
  orderId: number;
}) {
  const assign = useAssignDocument();
  const [ziel, setZiel] = useState<Record<number, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  if (p.docs.length === 0) return null;
  return (
    <Zeile titel="Sonstige">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {p.docs.map((d) => {
          const istLosePackliste = d.typ === "packing_list" && d.purchase_shipment_id === null;
          return (
            <div key={d.id} className="flex flex-wrap items-center gap-2">
              <DokumentChip doc={d} />
              {istLosePackliste ? p.shipments.length > 0 ? (
                <>
                  <select
                    aria-label={`Container für Packliste ${d.id}`}
                    value={ziel[d.id] ?? ""}
                    onChange={(event) => setZiel((current) => ({ ...current, [d.id]: event.target.value }))}
                    className="rounded border border-line bg-background px-2 py-1 text-xs"
                  >
                    <option value="">– Container wählen –</option>
                    {p.shipments.map((shipment, index) => (
                      <option key={shipment.id} value={shipment.id}>
                        {shipment.label ?? `Container ${index + 1}`}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={assign.isPending || !ziel[d.id]}
                    onClick={() => {
                      const shipmentId = Number(ziel[d.id]);
                      if (!Number.isSafeInteger(shipmentId) || shipmentId <= 0) return;
                      setMeldung(null);
                      assign.mutate(
                        { docId: d.id, orderId: p.orderId, shipmentId },
                        {
                          onSuccess: () => setMeldung("Packliste wurde dem Container zugeordnet."),
                          onError: (error) => setMeldung(serverSatz(error, "Packliste konnte nicht zugeordnet werden")),
                        },
                      );
                    }}
                  >
                    Container zuordnen
                  </Button>
                </>
              ) : (
                <span className="text-ink-2">Zuerst einen Container in der Akte anlegen.</span>
              ) : null}
            </div>
          );
        })}
        {meldung ? <Hinweis text={meldung} /> : null}
      </div>
    </Zeile>
  );
}

export function NotizZeile(p: { file: PurchaseOrderFile; darfPreise: boolean }) {
  const patch = usePatchPurchaseOrder(p.file.id);
  return (
    <Zeile titel="Notiz">
      <InlineText
        mehrzeilig
        value={p.file.note}
        ariaLabel="Notiz zur Bestellung"
        disabled={!p.darfPreise}
        disabledGrund="Ändern der Bestellung braucht die Einkaufs-Preisrechte"
        onSave={(v) => patch.mutateAsync({ note: v })}
      />
    </Zeile>
  );
}
